import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useState, useRef, useEffect, useReducer, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles, Modal, Text, Icon, Button, SelectWrapper, Tooltip } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { deleteExtraFieldTab, deleteExtraField, updateExtraFieldsOrder } from "../../../lib/services/fields";
import { getFilterSchema, getColumns, getFilteredList, getTabsOptions, getDynamicFieldOptions, ENABLED_DUPLICATE_ENTITIES } from "../lib";
import { subscribe } from "../../../lib/EventBuser";
import { FIELDS_REFRESH } from "../../../lib/events";
import { showErrorToast } from "../../../lib/errors";
import { FIELD_TYPE_TAB, FIELD_TYPE_STANDARD, FIELD_TYPE_EXTRA } from "../../../lib/constants";
import { getParameter } from "../../../lib/services/parameters";
import SectionFilter from "../../../components/SectionFilter";
import SectionList from "../../../components/SectionList";
import SectionToolbar from "../../../components/SectionToolbar";
import SectionToolbarSearch from "../../../components/SectionToolbar/SectionToolbarSearch";
import FiltersButton, { getNumberFilters } from "../../../components/Filters/FiltersButton";
import { intercomHideForList } from "../../../utils/custom";
import { getFieldTypeIcon } from "../../../utils/icons";
import { successToast } from "../../../utils/toast";
import FieldsTabs from "./FieldsTabs";
import FieldsIcon from "../icons/FieldsIcon";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'FieldsLists');
const initialState = {
  list: [],
  filteredList: [],
  isLoading: true,
  hasError: false,
  isFiltersOpen: false,
  removeModal: {
    isLoading: false,
    isOpen: false,
    row: null,
    tabWithFields: false
  },
  filterSchema: [],
  numberFilters: 0,
  haveFilters: false,
  generalFilterValue: null
};
function reducer(state, action) {
  switch (action.type) {
    case 'init':
      return _objectSpread(_objectSpread({}, state), {}, {
        list: action.list,
        filteredList: action.filteredList,
        isLoading: false
      });
    case 'setSchemaOptions':
      let newFilterSchema = JSON.parse(JSON.stringify(state.filterSchema));
      const optionsMap = {
        branches: action.branches,
        tabId: action.tabOptions,
        dynamicField: action.dynamicFieldOptions
      };
      newFilterSchema.forEach(item => {
        const id = item.id;
        if (optionsMap[id]) item.fields[0].inputProps.options = optionsMap[id];
      });
      return _objectSpread(_objectSpread({}, state), {}, {
        filterSchema: newFilterSchema
      });
    case 'setFilteredList':
      return _objectSpread(_objectSpread({}, state), {}, {
        filteredList: action.filteredList,
        numberFilters: action.numberFilters,
        haveFilters: action.haveFilters
      });
    case 'setGeneralFilterValue':
      return _objectSpread(_objectSpread({}, state), {}, {
        filteredList: action.filteredList,
        generalFilterValue: action.generalFilterValue
      });
    case 'setError':
      return _objectSpread(_objectSpread({}, state), {}, {
        hasError: action.error,
        isLoading: false
      });
    case 'setLoading':
      return _objectSpread(_objectSpread({}, state), {}, {
        isLoading: action.value,
        filteredList: action.resetList ? [] : state.filteredList
      });
    case 'setIsFiltersOpen':
      return _objectSpread(_objectSpread({}, state), {}, {
        isFiltersOpen: action.value
      });
    case 'setRemoveModal':
      return _objectSpread(_objectSpread({}, state), {}, {
        removeModal: action.value
      });
    default:
      throw new Error('No action provided');
  }
}
const FieldsLists = _ref => {
  var _state$generalFilterV2, _permissions$extraFie0, _state$data2, _state$data2$entity;
  let {
    entity,
    activeTab,
    activeShrinkedTab,
    branches,
    onChangeTab,
    onChangeShrinked,
    onEditStandardField,
    onCreateExtraField,
    onEditExtraField,
    onCreateFieldsTab,
    onEditFieldsTab,
    onReorderTabs,
    fieldsData,
    entityData,
    getEntityData,
    onReorderExtraFields,
    idSetupRole,
    activeRowId,
    setActiveRowId,
    entityLogName,
    entityLogSubmodule,
    overrides,
    onUpdate,
    onOpenUpgradeTabsInfo
  } = _ref;
  const theme = useTheme();
  const classes = useStyles();
  const isFirstTime = useRef(false);
  const currentEntity = useRef(entity);
  const columnIDsToHide = useMemo(() => {
    let columnIDsToHide = overrides === null || overrides === void 0 ? void 0 : overrides.columnIDsToHide;
    return columnIDsToHide && Array.isArray(columnIDsToHide) ? columnIDsToHide : null;
  }, [overrides]);
  const {
    t,
    language,
    permissions,
    logEvent,
    overridePermissions,
    isWeb,
    canCreateTabs,
    customGeneralTab,
    showCrud,
    tracking
  } = useWeb5Context({
    overrides
  });
  const [state, dispatch] = useReducer(reducer, _objectSpread(_objectSpread({}, initialState), {}, {
    filterSchema: getFilterSchema(t, columnIDsToHide)
  }));
  const [intercomRef, setIntercomRef] = useState('show');
  const filterValues = useRef();
  const entities = useRef();
  const tabs = useRef();
  const setEntities = useCallback(value => {
    entities.current = value;
  }, []);
  const callGetEntityData = useCallback(() => {
    dispatch({
      type: 'setLoading',
      value: true,
      resetList: entity !== currentEntity.current
    });
    currentEntity.current = entity;
    Promise.all([getParameter('implementation', 'CheckDuplicateFields'), getEntityData(entity)]).then(_ref2 => {
      var _parameter$data, _parameter$data2;
      let [parameter, res] = _ref2;
      // Checking duplicate param is disabled
      if ((parameter === null || parameter === void 0 ? void 0 : (_parameter$data = parameter.data) === null || _parameter$data === void 0 ? void 0 : _parameter$data.value) === '0' || !(parameter !== null && parameter !== void 0 && (_parameter$data2 = parameter.data) !== null && _parameter$data2 !== void 0 && _parameter$data2.value)) res.list.map(item => item.checkDuplicates = false);
      dispatch({
        type: 'init',
        list: res.list,
        filteredList: filterValues.current ? getFilteredList(res.list, filterValues.current) : res.list
      });
    }).catch(err => {
      console.warn(err);
      dispatch({
        type: 'setError',
        value: true
      });
    }).then(() => isFirstTime.current = true).then(() => dispatch({
      type: 'setLoading',
      value: false
    }));
  }, [entity, getEntityData]);
  useEffect(() => callGetEntityData(), [callGetEntityData]);
  useEffect(() => {
    var _entityData$fields, _entityData$fields2;
    if (!branches || !(entityData !== null && entityData !== void 0 && entityData.list) || !(entityData !== null && entityData !== void 0 && (_entityData$fields = entityData.fields) !== null && _entityData$fields !== void 0 && _entityData$fields.tab) || !isFirstTime.current) return;
    isFirstTime.current = false;
    const tabOptions = getTabsOptions(entityData === null || entityData === void 0 ? void 0 : (_entityData$fields2 = entityData.fields) === null || _entityData$fields2 === void 0 ? void 0 : _entityData$fields2.tab, t, customGeneralTab) || [];
    const dynamicFieldOptions = getDynamicFieldOptions(entityData === null || entityData === void 0 ? void 0 : entityData.list);
    dispatch({
      type: 'setSchemaOptions',
      branches,
      tabOptions,
      dynamicFieldOptions
    });
  }, [t, language, state, entityData, branches, customGeneralTab]);
  useEffect(() => {
    return subscribe(FIELDS_REFRESH, () => callGetEntityData());
  }, [callGetEntityData]);

  // Create action
  const onClickCreateFieldsTab = useCallback(() => {
    onCreateFieldsTab(entities.current);
    setTimeout(() => {
      document.dispatchEvent(new Event('click'));
    });
  }, [onCreateFieldsTab]);
  const onClickCreateExtraField = useCallback(() => {
    onCreateExtraField(entities.current);
    setTimeout(() => {
      document.dispatchEvent(new Event('click'));
    });
    (tracking === null || tracking === void 0 ? void 0 : tracking.create) && tracking.create();
  }, [onCreateExtraField, tracking]);

  // Edit action
  const onEdit = useCallback(row => {
    switch (row.fieldType) {
      case FIELD_TYPE_TAB:
        onEditFieldsTab(row, entities.current);
        break;
      case FIELD_TYPE_STANDARD:
        onEditStandardField(row, entities.current);
        (tracking === null || tracking === void 0 ? void 0 : tracking.update) && tracking.update();
        break;
      case FIELD_TYPE_EXTRA:
        onEditExtraField(row, entities.current, tabs.current, entity);
        (tracking === null || tracking === void 0 ? void 0 : tracking.update) && tracking.update();
        break;
      default:
        break;
    }
  }, [entity, onEditExtraField, onEditFieldsTab, onEditStandardField, tracking]);
  const groupBy = (array, key) => {
    return array.reduce((result, obj) => {
      if (obj[key]) (result[obj[key]] = result[obj[key]] || []).push(obj.idDb);
      return result;
    }, {});
  };

  // Duplicate action
  const onDuplicate = useCallback(row => {
    logEvent && logEvent({
      event: entityLogName,
      submodule: entityLogSubmodule,
      functionality: 'duplicate'
    });
    onEditExtraField(row, entities.current, tabs.current, entity, true);
    onUpdate && onUpdate();
  }, [entity, entityLogName, entityLogSubmodule, logEvent, onEditExtraField, onUpdate]);

  // Reorder tabs action
  const onReorder = useCallback(row => {
    onReorderTabs(entity, row);
  }, [entity, onReorderTabs]);

  // Remove action
  const onCloseRemoveModal = useCallback(() => {
    var _state$removeModal, _state$removeModal2;
    setActiveRowId(null);
    dispatch({
      type: 'setRemoveModal',
      value: {
        isLoading: false,
        isOpen: false,
        row: state === null || state === void 0 ? void 0 : (_state$removeModal = state.removeModal) === null || _state$removeModal === void 0 ? void 0 : _state$removeModal.row,
        tabWithFields: state === null || state === void 0 ? void 0 : (_state$removeModal2 = state.removeModal) === null || _state$removeModal2 === void 0 ? void 0 : _state$removeModal2.tabWithFields
      }
    });
  }, [state, setActiveRowId]);
  const onRemove = useCallback(row => {
    setActiveRowId(row.id);
    let tabWithFields = false;
    const idGroups = groupBy(state === null || state === void 0 ? void 0 : state.list, 'tabId');
    if ((row === null || row === void 0 ? void 0 : row.fieldType) === FIELD_TYPE_TAB && idGroups[row.idDb]) tabWithFields = true;
    dispatch({
      type: 'setRemoveModal',
      value: {
        isLoading: false,
        isOpen: true,
        row,
        tabWithFields
      }
    });
    (tracking === null || tracking === void 0 ? void 0 : tracking.delete) && tracking.delete();
  }, [state, setActiveRowId, tracking]);
  const onConfirmRemoveModal = useCallback(() => {
    dispatch({
      type: 'setRemoveModal',
      value: {
        isLoading: true,
        isOpen: true,
        row: state.removeModal.row
      }
    });
    if (state.removeModal.row.fieldType === FIELD_TYPE_TAB) {
      deleteExtraFieldTab(state.removeModal.row.idDb).then(() => {
        successToast({
          t,
          text: t('cfm_success_delete_tab')
        });
        callGetEntityData();
        onUpdate && onUpdate();
      }).catch(error => showErrorToast(t, error)).then(() => onCloseRemoveModal());
    } else {
      logEvent({
        event: entityLogName,
        submodule: entityLogSubmodule,
        functionality: 'delete'
      });
      deleteExtraField(state.removeModal.row.idDb).then(() => {
        successToast({
          t,
          text: t('cfm_success_delete_field')
        });
        callGetEntityData();
        onUpdate && onUpdate();
      }).catch(error => {
        showErrorToast(t, error);
      }).then(() => {
        onCloseRemoveModal();
      });
    }
  }, [logEvent, entityLogName, entityLogSubmodule, state.removeModal.row, t, callGetEntityData, onCloseRemoveModal, onUpdate]);
  const tableParams = useMemo(() => {
    return {
      onRowDrag: params => params.data.fieldType === FIELD_TYPE_EXTRA
    };
  }, []);
  const getAvatarSvg = useCallback(data => {
    return getFieldTypeIcon(data.cellType);
  }, []);

  // List configuration
  const columns = useMemo(() => {
    let columns = getColumns({
      t,
      params: tableParams,
      onEdit,
      getAvatarSvg,
      hasFilters: state.haveFilters,
      hasGeneralFilter: state.generalFilterValue,
      entity,
      permissions: permissions === null || permissions === void 0 ? void 0 : permissions.extraFields,
      overridePermissions,
      isEnabledDuplicateEntities: ENABLED_DUPLICATE_ENTITIES,
      columnIDsToHide
    });
    return columns;
  }, [t, tableParams, onEdit, getAvatarSvg, state.haveFilters, state.generalFilterValue, entity, permissions === null || permissions === void 0 ? void 0 : permissions.extraFields, overridePermissions, columnIDsToHide]);
  const actions = useMemo(() => {
    return [row => {
      var _permissions$extraFie, _permissions$extraFie2, _permissions$extraFie3, _permissions$extraFie4;
      if (row.fieldType === FIELD_TYPE_EXTRA && (overridePermissions && row !== null && row !== void 0 && row.isEditable || permissions !== null && permissions !== void 0 && (_permissions$extraFie = permissions.extraFields) !== null && _permissions$extraFie !== void 0 && _permissions$extraFie.update && (row !== null && row !== void 0 && row.isEditable && !(permissions !== null && permissions !== void 0 && (_permissions$extraFie2 = permissions.extraFields) !== null && _permissions$extraFie2 !== void 0 && _permissions$extraFie2.userUpdate) || permissions !== null && permissions !== void 0 && (_permissions$extraFie3 = permissions.extraFields) !== null && _permissions$extraFie3 !== void 0 && _permissions$extraFie3.userUpdate))) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__edit-btn",
          onClick: () => {
            setIntercomRef(false);
            onEdit(row, entities);
          },
          name: "edit"
        }),
        tooltip: {
          content: t('cfm_action_edit')
        }
      };
      if (row.fieldType === FIELD_TYPE_STANDARD && (overridePermissions || permissions !== null && permissions !== void 0 && (_permissions$extraFie4 = permissions.extraFields) !== null && _permissions$extraFie4 !== void 0 && _permissions$extraFie4.updateStandard)) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__edit-btn",
          onClick: () => {
            setIntercomRef(false);
            onEdit(row, entities);
          },
          name: "edit"
        }),
        tooltip: {
          content: t('cfm_action_edit')
        }
      };
      if (row.fieldType === FIELD_TYPE_TAB && row.idDb !== -1) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__edit-btn",
          onClick: () => {
            setIntercomRef(false);
            onEdit(row, entities);
          },
          name: "edit"
        }),
        tooltip: {
          content: t('cfm_action_edit')
        }
      };
    }, row => {
      if (row.fieldType === FIELD_TYPE_TAB && row.idDb !== -1) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__reorder-btn",
          onClick: () => onReorder(row),
          name: "draggableSort"
        }),
        tooltip: {
          content: t('cfm_label_sort_tabs')
        }
      };
    }, row => {
      var _permissions$extraFie5;
      if (row.fieldType === FIELD_TYPE_EXTRA && (overridePermissions || permissions !== null && permissions !== void 0 && (_permissions$extraFie5 = permissions.extraFields) !== null && _permissions$extraFie5 !== void 0 && _permissions$extraFie5.create)) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__duplicate-btn",
          onClick: () => onDuplicate(row),
          name: "duplicate"
        }),
        tooltip: {
          content: t('cfm_action_duplicate')
        }
      };
    }, row => {
      var _permissions$extraFie6, _permissions$extraFie7, _permissions$extraFie8;
      if (row.fieldType === FIELD_TYPE_TAB && row.idDb !== -1) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__delete-btn",
          onClick: () => onRemove(row),
          name: "trash"
        }),
        tooltip: {
          content: t('cfm_action_delete')
        }
      };
      if (row.fieldType === FIELD_TYPE_EXTRA && (overridePermissions && row !== null && row !== void 0 && row.isEditable || permissions !== null && permissions !== void 0 && (_permissions$extraFie6 = permissions.extraFields) !== null && _permissions$extraFie6 !== void 0 && _permissions$extraFie6.delete && (row !== null && row !== void 0 && row.isEditable && !(permissions !== null && permissions !== void 0 && (_permissions$extraFie7 = permissions.extraFields) !== null && _permissions$extraFie7 !== void 0 && _permissions$extraFie7.userUpdate) || permissions !== null && permissions !== void 0 && (_permissions$extraFie8 = permissions.extraFields) !== null && _permissions$extraFie8 !== void 0 && _permissions$extraFie8.userUpdate))) return {
        icon: /*#__PURE__*/React.createElement(Icon, {
          className: "fields__delete-btn",
          onClick: () => onRemove(row),
          name: "trash"
        }),
        tooltip: {
          content: t('cfm_action_delete')
        }
      };
    }];
  }, [permissions, overridePermissions, t, onEdit, onReorder, onDuplicate, onRemove]);
  const openFilters = useCallback(() => {
    dispatch({
      type: 'setIsFiltersOpen',
      value: !state.isFiltersOpen
    });
  }, [state.isFiltersOpen]);
  const onChangeFilters = useCallback(values => {
    var _fieldsData$entity, _state$generalFilterV;
    filterValues.current = _objectSpread(_objectSpread({}, filterValues.current), values);
    const numberFilters = getNumberFilters(values);
    if (numberFilters > 0) {
      logEvent({
        event: entityLogName,
        submodule: entityLogSubmodule,
        functionality: 'filter'
      });
    } else {
      filterValues.current = {};
    }
    dispatch({
      type: 'setFilteredList',
      filteredList: getFilteredList((_fieldsData$entity = fieldsData[entity]) === null || _fieldsData$entity === void 0 ? void 0 : _fieldsData$entity.list, _objectSpread(_objectSpread({}, values), {}, {
        name: (_state$generalFilterV = state.generalFilterValue) === null || _state$generalFilterV === void 0 ? void 0 : _state$generalFilterV.value
      })),
      numberFilters,
      haveFilters: numberFilters > 0
    });
  }, [entity, entityLogName, entityLogSubmodule, fieldsData, logEvent, (_state$generalFilterV2 = state.generalFilterValue) === null || _state$generalFilterV2 === void 0 ? void 0 : _state$generalFilterV2.value]);
  const generalSearchOptions = useMemo(() => state.list.map(el => _objectSpread(_objectSpread({}, el), {}, {
    label: el['name'],
    value: el.id
  })), [state.list]);
  const onChangeGeneralSearch = useCallback((value, info) => {
    filterValues.current = _objectSpread(_objectSpread({}, filterValues.current), {}, {
      name: null
    });
    if (value && info && info.action === 'select-option') {
      onEdit(value);
    }
    if (!value && info && info.action === 'clear') {
      var _fieldsData$entity2;
      dispatch({
        type: 'setGeneralFilterValue',
        filteredList: getFilteredList((_fieldsData$entity2 = fieldsData[entity]) === null || _fieldsData$entity2 === void 0 ? void 0 : _fieldsData$entity2.list, _objectSpread(_objectSpread({}, filterValues.current), {}, {
          name: null
        })),
        generalFilterValue: null
      });
    }
  }, [entity, fieldsData, onEdit]);
  const onBlurGeneralSearch = useCallback(value => {
    var _fieldsData$entity3;
    logEvent({
      event: entityLogName,
      submodule: entityLogSubmodule,
      functionality: 'search'
    });
    filterValues.current = _objectSpread(_objectSpread({}, filterValues.current), {}, {
      name: value
    });
    dispatch({
      type: 'setGeneralFilterValue',
      filteredList: getFilteredList((_fieldsData$entity3 = fieldsData[entity]) === null || _fieldsData$entity3 === void 0 ? void 0 : _fieldsData$entity3.list, _objectSpread(_objectSpread({}, filterValues.current), {}, {
        name: value
      })),
      generalFilterValue: value ? {
        label: value,
        value
      } : null
    });
  }, [entity, entityLogName, entityLogSubmodule, fieldsData, logEvent]);
  const loadGeneralSearchOptions = useCallback(value => Promise.resolve(generalSearchOptions === null || generalSearchOptions === void 0 ? void 0 : generalSearchOptions.filter(current => {
    var _current$label;
    return (_current$label = current.label) === null || _current$label === void 0 ? void 0 : _current$label.toLowerCase().includes(value === null || value === void 0 ? void 0 : value.toLowerCase());
  })), [generalSearchOptions]);
  const onChangeCreate = useCallback(_ref3 => {
    let {
      value
    } = _ref3;
    if (value === 'create_extra_field') onClickCreateExtraField();
    if (value === 'create_tab') {
      if (isWeb && !canCreateTabs) onOpenUpgradeTabsInfo();else onClickCreateFieldsTab();
    }
  }, [onClickCreateExtraField, onClickCreateFieldsTab, canCreateTabs, onOpenUpgradeTabsInfo, isWeb]);
  const createOptions = useMemo(() => [{
    label: t('cfm_title_create_extra_field'),
    value: 'create_extra_field',
    className: 'fields__create-extra-field-btn'
  }, {
    label: t('cfm_action_create_tab'),
    value: 'create_tab',
    className: 'fields__create-tab-btn'
  }], [t]);
  const searchPlaceholder = useMemo(() => `${t('cfm_placeholder_search')} ${t('cfm_label_field_name').toLowerCase()}`, [t]);
  const toolbarItems = useMemo(() => {
    var _permissions$extraFie9;
    const items = [];
    const supportLink = isWeb ? 'link_custom_fields_help_center_web' : 'link_custom_fields_help_center';
    items.push({
      component: /*#__PURE__*/React.createElement(SectionToolbarSearch, {
        className: "fields__toolbar__search",
        value: state.generalFilterValue,
        placeholder: searchPlaceholder,
        loadOptions: loadGeneralSearchOptions,
        onChange: onChangeGeneralSearch,
        onBlurSearch: onBlurGeneralSearch
      }),
      position: 'left'
    });
    items.push({
      component: /*#__PURE__*/React.createElement(Tooltip, {
        placement: "top",
        content: /*#__PURE__*/React.createElement("span", null, t('label_help_center'))
      }, /*#__PURE__*/React.createElement(Button, {
        type: "terciary",
        icon: "helpOutline",
        onClick: () => window.open(t(supportLink), '_blank')
      })),
      position: 'right'
    });
    items.push({
      component: /*#__PURE__*/React.createElement(FiltersButton, {
        haveFilters: state.haveFilters,
        numberFilters: state.numberFilters,
        openFilters: openFilters
      }),
      position: 'right'
    });
    if (overridePermissions || permissions !== null && permissions !== void 0 && (_permissions$extraFie9 = permissions.extraFields) !== null && _permissions$extraFie9 !== void 0 && _permissions$extraFie9.create) {
      items.push({
        component: /*#__PURE__*/React.createElement(SelectWrapper, {
          options: createOptions,
          onChange: onChangeCreate
        }, /*#__PURE__*/React.createElement(Button, {
          icon: "arrowDropDown",
          iconPosition: "right",
          className: "fields__toolbar__create-btn"
        }, t('cfm_action_create'))),
        position: 'right'
      });
    }
    return items;
  }, [isWeb, state.generalFilterValue, state.haveFilters, state.numberFilters, searchPlaceholder, loadGeneralSearchOptions, onChangeGeneralSearch, onBlurGeneralSearch, t, openFilters, overridePermissions, permissions === null || permissions === void 0 ? void 0 : (_permissions$extraFie0 = permissions.extraFields) === null || _permissions$extraFie0 === void 0 ? void 0 : _permissions$extraFie0.create, createOptions, onChangeCreate]);
  const rowClassRules = useMemo(() => ({
    'row-tab': function (params) {
      var _params$data;
      return (params === null || params === void 0 ? void 0 : (_params$data = params.data) === null || _params$data === void 0 ? void 0 : _params$data.fieldType) === FIELD_TYPE_TAB;
    }
  }), []);
  const onCloseFilters = useCallback(() => dispatch({
    type: 'setIsFiltersOpen',
    value: false
  }), []);
  const ReadOnlyHeader = useCallback(() => /*#__PURE__*/React.createElement(Icon, {
    className: "fields__readonly-icon",
    name: "lock",
    color: theme.colors.grey[700]
  }), [theme.colors.grey]);
  const VisibleHeader = useCallback(() => /*#__PURE__*/React.createElement(Icon, {
    className: "fields__visible-icon",
    name: "visibilityOn",
    color: theme.colors.grey[700]
  }), [theme.colors.grey]);
  const MandatoryHeader = useCallback(() => /*#__PURE__*/React.createElement(Icon, {
    className: "fields__asterisk-icon",
    name: "asterisk",
    color: theme.colors.grey[700]
  }), [theme.colors.grey]);
  const DuplicatesHeader = useCallback(() => /*#__PURE__*/React.createElement(Icon, {
    className: "fields__duplicates-icon",
    name: "duplicates",
    color: theme.colors.grey[700]
  }), [theme.colors.grey]);
  const emptyViewProps = useMemo(() => ({
    icon: /*#__PURE__*/React.createElement(FieldsIcon, {
      color: theme.colors.grey[100]
    })
  }), [theme]);
  const headerComps = useMemo(() => ({
    ReadOnlyHeader,
    VisibleHeader,
    MandatoryHeader,
    DuplicatesHeader
  }), [DuplicatesHeader, MandatoryHeader, ReadOnlyHeader, VisibleHeader]);
  const modalOverrides = {
    confirmButton: {
      type: 'primary-error',
      isLoading: state.removeModal.isLoading
    }
  };
  const getDeleteBody = useMemo(() => {
    var _state$removeModal3, _state$removeModal3$r, _state$removeModal4, _state$removeModal6, _state$removeModal6$r, _state$removeModal7;
    if ((state === null || state === void 0 ? void 0 : (_state$removeModal3 = state.removeModal) === null || _state$removeModal3 === void 0 ? void 0 : (_state$removeModal3$r = _state$removeModal3.row) === null || _state$removeModal3$r === void 0 ? void 0 : _state$removeModal3$r.fieldType) === FIELD_TYPE_TAB && state !== null && state !== void 0 && (_state$removeModal4 = state.removeModal) !== null && _state$removeModal4 !== void 0 && _state$removeModal4.tabWithFields) {
      var _state$removeModal5, _state$removeModal5$r;
      return t('cfm_label_can_not_delete_tab', [state === null || state === void 0 ? void 0 : (_state$removeModal5 = state.removeModal) === null || _state$removeModal5 === void 0 ? void 0 : (_state$removeModal5$r = _state$removeModal5.row) === null || _state$removeModal5$r === void 0 ? void 0 : _state$removeModal5$r.description]);
    } else if ((state === null || state === void 0 ? void 0 : (_state$removeModal6 = state.removeModal) === null || _state$removeModal6 === void 0 ? void 0 : (_state$removeModal6$r = _state$removeModal6.row) === null || _state$removeModal6$r === void 0 ? void 0 : _state$removeModal6$r.fieldType) === FIELD_TYPE_TAB && !(state !== null && state !== void 0 && (_state$removeModal7 = state.removeModal) !== null && _state$removeModal7 !== void 0 && _state$removeModal7.tabWithFields)) {
      var _state$removeModal8, _state$removeModal8$r;
      return t('cfm_label_confirm_delete_tab', [state === null || state === void 0 ? void 0 : (_state$removeModal8 = state.removeModal) === null || _state$removeModal8 === void 0 ? void 0 : (_state$removeModal8$r = _state$removeModal8.row) === null || _state$removeModal8$r === void 0 ? void 0 : _state$removeModal8$r.description]);
    } else {
      return t('cfm_label_confirm_delete_field');
    }
  }, [state, t]);
  const dragGroups = useMemo(() => {
    const groups = _objectSpread({}, entityData === null || entityData === void 0 ? void 0 : entityData.extraOrder);
    Object.keys(groups).forEach(key => {
      groups[key] = groups[key].map(el => `e${el}`);
    });
    return {
      groups,
      groupsKey: 'tabId'
    };
  }, [entityData]);
  const onDragEnd = useCallback((event, data) => {
    logEvent({
      event: entityLogName,
      submodule: entityLogSubmodule,
      functionality: 'sortField'
    });
    const group = [...entityData.extraOrder[event.node.data.tabId]];
    const from = group.indexOf(event.node.data.idDb);
    const to = group.indexOf(data.rowDataBefore[event.overIndex].idDb);
    const element = group.splice(from, 1)[0];
    group.splice(to, 0, element);
    const groupOrder = group.map((el, i) => ({
      id: el,
      order: i
    }));
    return updateExtraFieldsOrder(groupOrder).then(() => {
      onReorderExtraFields(event.node.data.tabId, group);
    });
  }, [entityData === null || entityData === void 0 ? void 0 : entityData.extraOrder, entityLogName, entityLogSubmodule, logEvent, onReorderExtraFields]);
  const getRowNodeId = useCallback(data => {
    return data.id;
  }, []);
  const getActiveRowId = useCallback(() => {
    return activeRowId;
  }, [activeRowId]);
  const placeholderOptions = useMemo(() => ({
    avatars: false
  }), []);
  const onCellMouseOver = useCallback(data => {
    var _window$userflow;
    const hideShow = intercomHideForList(data, state.filteredList, 4);
    setIntercomRef(hideShow);
    (_window$userflow = window.userflow) === null || _window$userflow === void 0 ? void 0 : _window$userflow.setResourceCenterLauncherHidden(hideShow === 'hide');
  }, [state.filteredList]);
  const listProps = useMemo(() => {
    var _state$data, _state$data$entity;
    return {
      id: `fields-list-${(_state$data = state.data) === null || _state$data === void 0 ? void 0 : (_state$data$entity = _state$data.entity) === null || _state$data$entity === void 0 ? void 0 : _state$data$entity.value}`,
      onCellMouseOver: onCellMouseOver,
      className: classes.list,
      columns: columns,
      rows: state.filteredList,
      actions: actions,
      isLoading: state.isLoading,
      hasError: state.hasError,
      headerHeight: 40,
      rowHeight: 40,
      rowClassRules: rowClassRules,
      emptyViewProps: emptyViewProps,
      components: headerComps,
      dragGroups: dragGroups,
      onDragEnd: onDragEnd,
      useDragRows: !state.haveFilters && !state.generalFilterValue,
      getRowNodeId: getRowNodeId,
      activeRowId: getActiveRowId(),
      useLoaderPlaceholder: true,
      placeholderOptions: placeholderOptions,
      intercomRef: intercomRef,
      withoutOffset: true
    };
  }, [actions, classes.list, columns, dragGroups, emptyViewProps, getActiveRowId, getRowNodeId, headerComps, intercomRef, onCellMouseOver, onDragEnd, placeholderOptions, rowClassRules, (_state$data2 = state.data) === null || _state$data2 === void 0 ? void 0 : (_state$data2$entity = _state$data2.entity) === null || _state$data2$entity === void 0 ? void 0 : _state$data2$entity.value, state.filteredList, state.generalFilterValue, state.hasError, state.haveFilters, state.isLoading]);
  useEffect(() => {
    setTimeout(() => {
      showCrud && (fieldsData === null || fieldsData === void 0 ? void 0 : fieldsData[entity]) && onClickCreateExtraField();
    });
  }, [showCrud, onClickCreateExtraField, fieldsData, entity]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(SectionFilter, {
    schema: state.filterSchema,
    isOpen: state.isFiltersOpen,
    onChange: onChangeFilters,
    onClose: onCloseFilters,
    id: "fields__section-filter__section"
  }), /*#__PURE__*/React.createElement(FieldsTabs, {
    onChange: onChangeTab,
    onChangeShrinked: onChangeShrinked,
    activeShrinkedTab: activeShrinkedTab,
    activeTab: activeTab,
    setEntities: setEntities,
    idSetupRole: idSetupRole,
    overrides: overrides
  }), /*#__PURE__*/React.createElement(SectionToolbar, {
    items: toolbarItems
  }), overrides && overrides.tablePreComponent, /*#__PURE__*/React.createElement("div", {
    className: classes.listContainer
  }, /*#__PURE__*/React.createElement(SectionList, listProps)), /*#__PURE__*/React.createElement(Modal, {
    title: state.removeModal.tabWithFields ? t('cfm_action_delete_tab') : t('action_delete'),
    size: "small",
    isOpen: state.removeModal.isOpen,
    onRequestClose: onCloseRemoveModal,
    onCancel: !state.removeModal.tabWithFields ? onCloseRemoveModal : null,
    onConfirm: state.removeModal.tabWithFields ? onCloseRemoveModal : onConfirmRemoveModal,
    cancelText: t('cfm_action_cancel'),
    confirmText: state.removeModal.tabWithFields ? t('action_got_it') : t('cfm_action_accept'),
    overrides: modalOverrides
  }, /*#__PURE__*/React.createElement(Text, null, getDeleteBody)));
};
process.env.NODE_ENV !== "production" ? FieldsLists.propTypes = {
  entity: PropTypes.string.isRequired,
  activeTab: PropTypes.string,
  activeShrinkedTab: PropTypes.object,
  onEditStandardField: PropTypes.func,
  onCreateExtraField: PropTypes.func,
  onEditExtraField: PropTypes.func,
  onCreateFieldsTab: PropTypes.func,
  onEditFieldsTab: PropTypes.func,
  onFilter: PropTypes.func
} : void 0;
export default /*#__PURE__*/memo(FieldsLists);